import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BookingStatistics from './BookingStatistics';

interface Board {
  id: string;
  boardNumber: number;
  name: string;
  isActive: boolean;
  openingHours: {
    [key: string]: { open: string; close: string; closed: boolean };
  };
}

interface Booking {
  id: string;
  boardId: string;
  venueId: string;
  userId: string;
  userName: string;
  startTime: Timestamp;
  endTime: Timestamp;
  status: 'confirmed' | 'cancelled' | 'completed';
  createdAt: Timestamp;
}

interface BookingCalendarProps {
  venueId: string;
  boards: Board[];
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({ venueId, boards }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [pricePerHour, setPricePerHour] = useState<number>(2500);

  const getEarliestOpeningHour = (): number => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[selectedDate.getDay()];
    const activeBoards = boards.filter(b => b.isActive);

    let earliestHour = 22; // Default to 10pm if no boards are open

    for (const board of activeBoards) {
      const hours = board.openingHours?.[dayName];
      if (hours && !hours.closed) {
        const openHour = parseInt(hours.open.split(':')[0]);
        if (openHour < earliestHour) {
          earliestHour = openHour;
        }
      }
    }

    return earliestHour;
  };

  const getLatestClosingHour = (): number => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[selectedDate.getDay()];
    const activeBoards = boards.filter(b => b.isActive);

    let latestHour = 5; // Default to 5am if no boards are open

    for (const board of activeBoards) {
      const hours = board.openingHours?.[dayName];
      if (hours && !hours.closed) {
        const closeHour = parseInt(hours.close.split(':')[0]);
        if (closeHour > latestHour) {
          latestHour = closeHour;
        }
      }
    }

    return latestHour;
  };

  const startHour = getEarliestOpeningHour();
  const endHour = getLatestClosingHour();
  const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => i + startHour);

  useEffect(() => {
    loadBookings();
    loadVenuePrice();
  }, [selectedDate, venueId]);

  const loadVenuePrice = async () => {
    if (!venueId) return;

    try {
      const venueDoc = await getDoc(doc(db, 'venues', venueId));
      if (venueDoc.exists()) {
        const venueData = venueDoc.data();
        setPricePerHour(venueData.pricePerHour || 2500);
      }
    } catch (error) {
      console.error('Error loading venue price:', error);
    }
  };

  const loadBookings = async () => {
    if (!venueId) return;

    setLoading(true);
    try {
      const startOfDay = new Date(selectedDate);
      startOfDay.setUTCHours(0, 0, 0, 0);

      const endOfDay = new Date(selectedDate);
      endOfDay.setUTCHours(23, 59, 59, 999);

      const q = query(
        collection(db, 'bookings'),
        where('venueId', '==', venueId),
        where('startTime', '>=', Timestamp.fromDate(startOfDay)),
        where('startTime', '<=', Timestamp.fromDate(endOfDay)),
        where('status', '==', 'confirmed')
      );

      const bookingsSnapshot = await getDocs(q);

      // Process bookings and fetch missing userNames
      const bookingPromises = bookingsSnapshot.docs.map(async (docSnapshot) => {
        const bookingData = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        } as Booking;

        // If userName is missing, fetch it from users collection
        if (!bookingData.userName && bookingData.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', bookingData.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              bookingData.userName = userData.username || userData.displayName || userData.email || 'Ismeretlen felhasználó';
            } else {
              bookingData.userName = 'Ismeretlen felhasználó';
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
            bookingData.userName = 'Ismeretlen felhasználó';
          }
        }

        return bookingData;
      });

      const resolvedBookings = await Promise.all(bookingPromises);
      setBookings(resolvedBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBookingsForSlot = (boardId: string, hour: number): Booking[] => {
    return bookings.filter(booking => {
      if (booking.boardId !== boardId) return false;

      const startHour = booking.startTime.toDate().getUTCHours();
      const startMinute = booking.startTime.toDate().getUTCMinutes();
      const endHour = booking.endTime.toDate().getUTCHours();
      const endMinute = booking.endTime.toDate().getUTCMinutes();

      const slotStart = hour;
      const slotEnd = hour + 1;

      const bookingStartDecimal = startHour + startMinute / 60;
      const bookingEndDecimal = endHour + endMinute / 60;

      return bookingStartDecimal < slotEnd && bookingEndDecimal > slotStart;
    });
  };

  const formatTime = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const changeDate = (days: number) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const getDayName = (date: Date): string => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayNames[date.getDay()];
  };

  const isBoardOpenOnDate = (board: Board, date: Date): boolean => {
    const dayName = getDayName(date);
    const hours = board.openingHours?.[dayName];
    if (!hours || hours.closed) return false;
    return true;
  };

  const isTimeSlotInOpeningHours = (board: Board, date: Date, hour: number): boolean => {
    const dayName = getDayName(date);
    const hours = board.openingHours?.[dayName];
    if (!hours || hours.closed) return false;

    const slotTime = `${hour.toString().padStart(2, '0')}:00`;
    return slotTime >= hours.open && slotTime < hours.close;
  };

  const activeBoards = boards.filter(b => b.isActive);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Section */}
      <BookingStatistics venueId={venueId} boards={boards} pricePerHour={pricePerHour} />

      {/* Calendar Section */}
      <div className="card-dark p-6">
        <h3 className="text-xl font-bold text-white mb-4">📅 Napi foglalások</h3>

        {/* Date Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => changeDate(-1)}
            className="px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
          >
            ← Előző nap
          </button>
          <div className="text-center">
            <h3 className="text-xl font-bold text-white">{formatDate(selectedDate)}</h3>
          </div>
          <button
            onClick={() => changeDate(1)}
            className="px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
          >
            Következő nap →
          </button>
        </div>

        {
          activeBoards.length === 0 ? (
            <div className="card-dark p-12 text-center">
              <p className="text-[#B8B8C3]">Nincsenek aktív táblák. Aktiváljon táblákat a Táblák kezelése fülön.</p>
            </div>
          ) : (
            <div className="card-dark p-4 overflow-x-auto">
              <div className="min-w-max">
                {/* Header Row */}
                <div className="grid gap-1 mb-1" style={{ gridTemplateColumns: `80px repeat(${activeBoards.length}, 200px)` }}>
                  <div className="bg-[#15191E] p-3 rounded font-bold text-white text-center">
                    Óra
                  </div>
                  {activeBoards.map(board => (
                    <div key={board.id} className="bg-[#15191E] p-3 rounded font-bold text-white text-center">
                      {board.name}
                    </div>
                  ))}
                </div>

                {/* Time Slots */}
                {hours.map(hour => (
                  <div
                    key={hour}
                    className="grid gap-1 mb-1"
                    style={{ gridTemplateColumns: `80px repeat(${activeBoards.length}, 200px)` }}
                  >
                    {/* Hour Label */}
                    <div className="bg-[#1d232a] p-3 rounded flex items-center justify-center text-white font-semibold">
                      {hour}:00
                    </div>

                    {/* Board Columns */}
                    {activeBoards.map(board => {
                      const slotBookings = getBookingsForSlot(board.id, hour);
                      const hasBooking = slotBookings.length > 0;
                      const isClosed = !isBoardOpenOnDate(board, selectedDate);
                      const isOutsideHours = !isTimeSlotInOpeningHours(board, selectedDate, hour);
                      const isUnavailable = isClosed || isOutsideHours;

                      return (
                        <div
                          key={board.id}
                          className={`p-2 rounded min-h-[60px] relative ${isUnavailable ? 'bg-[#15191E]' : hasBooking ? 'bg-[#4a5568]' : 'bg-[#31383D]'
                            }`}
                        >
                          {slotBookings.map(booking => {
                            const startTime = booking.startTime.toDate();
                            const endTime = booking.endTime.toDate();
                            const startHour = startTime.getUTCHours();
                            const startMinute = startTime.getUTCMinutes();
                            const endHour = endTime.getUTCHours();
                            const endMinute = endTime.getUTCMinutes();

                            // Only show booking in the slot where it starts
                            if (startHour === hour) {
                              // Calculate height based on duration
                              const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
                              const durationHours = durationMinutes / 60;
                              const heightPx = durationHours * 60; // 60px per hour

                              return (
                                <div
                                  key={booking.id}
                                  className="absolute left-2 right-2 bg-[#15191E] border-2 border-cyan-500 rounded p-2 text-xs text-white overflow-hidden z-10"
                                  style={{
                                    top: `${(startMinute / 60) * 60}px`,
                                    height: `${heightPx}px`
                                  }}
                                >
                                  <div className="font-semibold">{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</div>
                                  <div className="text-[#B8B8C3] truncate">{booking.userName}</div>
                                </div>
                              );
                            }
                            return null;
                          })}

                          {!hasBooking && !isUnavailable && (
                            <div className="text-center text-[#8a8f99] text-xs italic">
                              Elérhető
                            </div>
                          )}
                          {isUnavailable && (
                            <div className="text-center text-[#4a5568] text-xs italic">
                              {isClosed ? 'Zárva' : 'Nyitvatartáson kívül'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )
        }

        {/* Legend */}
        <div className="mt-6 p-4 bg-[#1d232a] rounded-lg">
          <h4 className="text-white font-semibold mb-3">Jelmagyarázat:</h4>
          <div className="flex gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#31383D] rounded border border-[#4a5568]"></div>
              <span className="text-[#B8B8C3]">Elérhető időpont</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#15191E] border-2 border-cyan-500 rounded"></div>
              <span className="text-[#B8B8C3]">Foglalt időpont</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
